import Checkbox from '@/components/elements/forms/Checkbox';
import { Switch } from '@/components/elements/forms/Switch';
import Header from '@/components/elements/Header/Header';
import { ListItem, ListItemContent } from '@/components/elements/lists';
import { SCREEN_NAME, SCREEN_VIEW } from '@/constants/analytics';
import { classnames } from '@/helpers';
import useCheckoutExperiment from '@/hooks/experiment/useCheckoutExperiment';
import useMobileView from '@/hooks/useMobileView';
import useTrackScreenShown from '@/hooks/useTrackScreenShown';
import { _s, _t } from '@/locale';
import Markdown from 'markdown-to-jsx';
import { Fragment, useState } from 'react';
import * as sanitizeHtml from 'sanitize-html';
import Modal from '../../modals/redesign/Modal/Modal';

type BookingTermsProps = {
  terms: string | React.ReactNode;
  termsAccepted: boolean;
  error?: boolean;
  onChange: (checked: boolean) => void;
  onClose: () => void;
};

const baseTranslationKey = 'components.modules.checkout.BookingTerms';

const BookingTermsVariantV2 = ({ terms, termsAccepted, error, onChange, onClose }: BookingTermsProps) => {
  const { isMobileView } = useMobileView();
  const [showTerms, setShowTerms] = useState(false);

  const markdown = sanitizeHtml(terms ?? '', {
    allowedTags: [],
    allowedAttributes: {},
  });

  useTrackScreenShown(
    { screen_name: SCREEN_VIEW.PRACTITIONER_TERMS, initiated_from: SCREEN_NAME.CHECKOUT_BOOKING_SUMMARY },
    [showTerms],
    [showTerms],
  );

  return (
    <Fragment>
      <div className="px-lg py-md gap-md flex flex-col">
        <Header label={_t(`${baseTranslationKey}.title`)} size="lg" />

        <Checkbox
          className={classnames(
            'p-xs pr-sm pl-md gap-md flex cursor-pointer flex-row items-center justify-between rounded-sm border',
            error && 'border-border_negative bg-bg_negative',
            !error && 'hover:bg-bg_secondary_bold active:bg-bg_secondary_pressed',
            termsAccepted ? 'border-border_selected bg-bg_secondary' : 'border-border_bold bg-bg_secondary_neutral',
          )}
          data-cy="bookingTermsButton"
          checked={termsAccepted}
          onChange={onChange}
          label={
            <span className="text-l flex-">
              {_t(`${baseTranslationKey}.description`)}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTerms(true);
                }}
                className="text-fg_link inline underline focus:outline-none focus:ring-0 focus-visible:ring-2">
                {_t(`${baseTranslationKey}.terms`)}
              </button>
            </span>
          }
        />
        {error && <p className="text-fg_negative text-m">{_t(`${baseTranslationKey}.error`)}</p>}
      </div>

      <Modal isOpen={showTerms}>
        <Modal.Content floating={!isMobileView}>
          <Modal.Header
            title={_t(`${baseTranslationKey}.modal.title`)}
            onClose={() => {
              setShowTerms(false);
              onClose();
            }}
          />
          <div className="px-xxl py-md md:p-0">
            <Markdown options={{ wrapper: TermsWrapper }}>{markdown}</Markdown>
          </div>
        </Modal.Content>
      </Modal>
    </Fragment>
  );
};

const BookingTermsVariantV1 = ({ terms, termsAccepted, error, onChange, onClose }: BookingTermsProps) => {
  const { isMobileView } = useMobileView();
  const [showTerms, setShowTerms] = useState(false);

  const markdown = sanitizeHtml(terms ?? '', {
    allowedTags: [],
    allowedAttributes: {},
  });

  useTrackScreenShown(
    { screen_name: SCREEN_VIEW.PRACTITIONER_TERMS, initiated_from: SCREEN_NAME.CHECKOUT_BOOKING_SUMMARY },
    [showTerms],
    [showTerms],
  );

  return (
    <div>
      <div className="px-lg py-md">
        <Header label={_s(`${baseTranslationKey}.title`)} size="lg" />
      </div>
      <ListItem
        className={`py-sm${error ? ' border-danger-500 border-b' : ''}`}
        rightSlot={
          <Switch
            switched={termsAccepted}
            onChange={() => onChange(!termsAccepted)}
            data-cy="bookingTermsButton"
            isCheckoutRedesign={true}
          />
        }>
        <ListItemContent
          subTitle={
            <span>
              {_s(`${baseTranslationKey}.description`)}
              <button onClick={() => setShowTerms(true)} className="text-information-700 inline underline">
                {_s(`${baseTranslationKey}.terms`)}
              </button>
            </span>
          }
        />
      </ListItem>
      {error && <p className="pl-lg pt-sm text-danger-500 text-xxs">{_s(`${baseTranslationKey}.error`)}</p>}
      <Modal isOpen={showTerms}>
        <Modal.Content floating={!isMobileView}>
          <Modal.Header
            title={_s(`${baseTranslationKey}.modal.title`)}
            onClose={() => {
              setShowTerms(false);
              onClose();
            }}
          />
          <div className="px-xxl py-md md:p-0">
            <Markdown options={{ wrapper: TermsWrapper }}>{markdown}</Markdown>
          </div>
        </Modal.Content>
      </Modal>
    </div>
  );
};

const BookingTermsVariantControl = ({ terms, termsAccepted, error, onChange, onClose }: BookingTermsProps) => {
  const { isMobileView } = useMobileView();
  const [showTerms, setShowTerms] = useState(false);

  const markdown = sanitizeHtml(terms ?? '', {
    allowedTags: [],
    allowedAttributes: {},
  });

  useTrackScreenShown(
    { screen_name: SCREEN_VIEW.PRACTITIONER_TERMS, initiated_from: SCREEN_NAME.CHECKOUT_BOOKING_SUMMARY },
    [showTerms],
    [showTerms],
  );

  return (
    <div>
      <div className="px-lg py-md">
        <Header label={_s(`${baseTranslationKey}.title`)} size="lg" />
      </div>
      <ListItem
        className={`py-sm${error ? ' border-danger-500 border-b' : ''}`}
        rightSlot={
          <Switch
            switched={termsAccepted}
            onChange={() => onChange(!termsAccepted)}
            data-cy="bookingTermsButton"
            isCheckoutRedesign={false}
          />
        }>
        <ListItemContent
          subTitle={
            <span>
              {_s(`${baseTranslationKey}.description`)}
              <button onClick={() => setShowTerms(true)} className="text-information-700 inline underline">
                {_s(`${baseTranslationKey}.terms`)}
              </button>
            </span>
          }
        />
      </ListItem>
      {error && <p className="pl-lg pt-sm text-danger-500 text-xxs">{_s(`${baseTranslationKey}.error`)}</p>}
      <Modal isOpen={showTerms}>
        <Modal.Content floating={!isMobileView}>
          <Modal.Header
            title={_s(`${baseTranslationKey}.modal.title`)}
            onClose={() => {
              setShowTerms(false);
              onClose();
            }}
          />
          <div className="px-xxl py-md md:p-0">
            <Markdown options={{ wrapper: TermsWrapper }}>{markdown}</Markdown>
          </div>
        </Modal.Content>
      </Modal>
    </div>
  );
};

const TermsWrapper = ({ children }) => {
  return <div className="space-y-sm relative flex flex-col">{children}</div>;
};

export default function BookingTermsExperimentWrapper(props: BookingTermsProps) {
  const { variant } = useCheckoutExperiment();

  if (variant === 'v2') {
    return <BookingTermsVariantV2 {...props} />;
  }

  if (variant === 'v1') {
    return <BookingTermsVariantV1 {...props} />;
  }

  return <BookingTermsVariantControl {...props} />;
}
